<template>
  <div class="v-modal">
    <div class="v-modal__header">
      <span>Import Locations</span>
      <div @click="close()" class="close"></div>
    </div>
    <div class="v-modal__body">
      <Errors @dismiss="dataErrors = []" :errors="csvErrors.concat(dataErrors)"/>
      <p>
        Assessment of your business <b>{{ tempScenario.name }}</b> starts with uploading a CSV file that contains its locations.
        <a href="#" v-if="!readMore" @click.prevent="readMore = true"><br/>[ read more ]</a>
        <span v-else>
          Any valid CSV is accepted, if the file has at least two columns, for latitude and longitude (WGS84), and at least two rows, the first row containing the column headers.
          Optionally, you can designate columns for the location's name, its annual water abstraction, effluent and loss (m<sup>3</sup>/yr).
          <a href="#" @click.prevent="readMore = false">[ read less ]</a>
        </span>
      </p>
      <div class="row mb-3">
        <label for="file" class="col-2 col-form-label">File</label>
        <div class="col-8">
          <input type="file" accept=".csv" @change="loadFile($event)" id="file" class="form-control"/>
        </div>
      </div>
      <div class="row mb-3">
        <label for="delimiter" class="col-2 col-form-label">Delimiter</label>
        <div class="col-2">
          <input type="text" v-model="parseConfig.delimiter" placeholder="[auto]" id="delimiter" class="form-control"/>
        </div>
        <div class="col-1"></div>
        <label for="quotechar" class="col-3 col-form-label">Quote Character</label>
        <div class="col-2">
          <input type="text" v-model="parseConfig.quoteChar" placeholder="&quot;" id="quotechar" class="form-control"/>
        </div>
      </div>
      <template v-if="content.length">
        <h5>Preview</h5>
        <div class="preview mb-3">
          <table class="table table-bordered mb-0">
            <thead>
              <tr>
                <th v-for="(cell, i) of content[0]" :key="'column' + i" :class="{reqcol: (i == latitudeColumn) || (i == longitudeColumn), optcol: (i == nameColumn) || (i == abstractionColumn) || (i == effluentColumn) || (i == lossColumn)}">
                  {{cell}}
                  <span v-if="i == latitudeColumn" class="sticker">lat</span>
                  <span v-if="i == longitudeColumn" class="sticker">long</span>
                  <span v-if="i == nameColumn" class="sticker">name</span>
                  <span v-if="i == abstractionColumn" class="sticker">abst</span>
                  <span v-if="i == effluentColumn" class="sticker">effl</span>
                  <span v-if="i == lossColumn" class="sticker">loss</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(line, j) of content">
                <tr v-if="j > 0" :key="'row' + j">
                  <td v-for="(cell, i) of line" :key="'cell' + i" :class="{reqcol: (i == latitudeColumn) || (i == longitudeColumn), optcol: (i == nameColumn) || (i == abstractionColumn) || (i == effluentColumn) || (i == lossColumn)}">
                    {{cell}}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <template v-if="!csvErrors.length">
          <h5>Required Columns</h5>
          <div class="row mb-3">
            <label for="latitude" class="col-2 col-form-label">Latitude (y)</label>
            <div class="col-4">
              <select v-model="latitudeColumn" @change="validate()" id="latitude" class="form-select">
                <option v-for="(cell, i) of content[0]" :key="'lat-header-' + i" :value="i">{{cell}}</option>
              </select>
              <div class="form-text">WGS84</div>
            </div>
            <label for="longitude" class="col-2 col-form-label pe-0">Longitude (x)</label>
            <div class="col-4">
              <select v-model="longitudeColumn" @change="validate()" id="longitude" class="form-select">
                <option v-for="(cell, i) of content[0]" :key="'long-header-' + i" :value="i">{{cell}}</option>
              </select>
              <div class="form-text">WGS84</div>
            </div>
          </div>
          <a href="#" v-if="!showOptionalColumns" @click.prevent="showOptionalColumns = true">[ show optional columns ]</a>
          <template v-else>
            <div class="d-flex align-items-baseline">
              <h5 class="me-1">Optional Columns</h5>
              <a href="#" @click.prevent="showOptionalColumns = false">[ hide ]</a>
            </div>
            <div class="row mb-3">
              <label for="name" class="col-2 col-form-label">Name</label>
              <div class="col-4">
                <select v-model="nameColumn" id="name" class="form-select">
                  <option :value="null">[none]</option>
                  <option v-for="(cell, i) of content[0]" :key="'name-header-' + i" :value="i">{{cell}}</option>
                </select>
              </div>
              <label for="abstraction" class="col-2 col-form-label">Abstraction</label>
              <div class="col-4">
                <select v-model="abstractionColumn" @change="validate()" id="abstraction" class="form-select">
                  <option :value="null">[none]</option>
                  <option v-for="(cell, i) of content[0]" :key="'abstraction-header-' + i" :value="i">{{cell}}</option>
                </select>
                <div class="form-text">m<sup>3</sup>/yr</div>
              </div>
            </div>
            <div class="row mb-3">
              <label for="effluent" class="col-2 col-form-label">Effluent</label>
              <div class="col-4">
                <select v-model="effluentColumn" @change="validate()" id="effluent" class="form-select">
                  <option :value="null">[none]</option>
                  <option v-for="(cell, i) of content[0]" :key="'effluent-header-' + i" :value="i">{{cell}}</option>
                </select>
                <div class="form-text">m<sup>3</sup>/yr</div>
              </div>
              <label for="loss" class="col-2 col-form-label">Loss</label>
              <div class="col-4">
                <select v-model="lossColumn" @change="validate()" id="loss" class="form-select">
                  <option :value="null">[none]</option>
                  <option v-for="(cell, i) of content[0]" :key="'loss-header-' + i" :value="i">{{cell}}</option>
                </select>
                <div class="form-text">m<sup>3</sup>/yr</div>
              </div>
            </div>
          </template>
        </template>
      </template>
    </div>
    <div class="v-modal__footer">
      <button @click="next()" :disabled="!!(!file || csvErrors.length)" title="Import locations and start new business assessment" class="btn btn-primary">Next</button>
    </div>
  </div>
</template>

<script>
  import Papa from "papaparse";
  import Errors from "@/components/shared/Errors.vue"
  import { closeModal, modalTypes, openModal } from "@/services/modal-model";

  export default {
    name: "Process",
    components: {
      Errors
    },
    props: {
      modal: {type: Object},
      tempScenario: {type: Object}
    },
    data() {
      return {
        readMore: false,
        showOptionalColumns: false,
        file: null,
        content: [],
        csvErrors: [],
        dataErrors: [],
        delimiter: null,  // Contains the actual (auto-detected) delimiter after parsing.
        parseConfig: {
          delimiter: "",
          quoteChar: '"',
          comments: "#",
          skipEmptyLines: true
        },
        latitudeColumn: null,
        longitudeColumn: null,
        nameColumn: null,
        abstractionColumn: null,
        effluentColumn: null,
        lossColumn: null
      }
    },
    computed: {
      unparseConfig() {
        return {
          delimiter: this.delimiter,
          quoteChar: this.parseConfig.quoteChar || '"'
        };
      }
    },
    watch: {
      "parseConfig.delimiter": function(val) {
        this.parseCsv();
      },
      "parseConfig.quoteChar": function(val) {
        this.parseCsv();
      }
    },
    methods: {
      close() {
        closeModal(this.modal);
      },
      loadFile(event) {
        this.file = event.target.files[0];
        this.parseCsv();
      },
      parseCsv() {
        if (this.file == null) {
          return;
        }

        const parseConfigBase = {
          complete: (results, file) => {
            this.content = results.data;
            this.csvErrors = results.errors.map(error => error.message + "." + ((error.row != null) ? (" Line " + error.row + ".") : "") + ((error.index != null) ? (" Cell " + error.index + ".") : ""));
            this.dataErrors = [];
            this.delimiter = results.meta.delimiter;
            this.checkAdditionalCsvErrors();
            this.latitudeColumn = null;
            this.longitudeColumn = null;
            this.nameColumn = null;
            this.abstractionColumn = null;
            this.effluentColumn = null;
            this.lossColumn = null;
            this.guessColumns();
            this.validate();
          }
        };
        Papa.parse(this.file, Object.assign(parseConfigBase, this.parseConfig));
      },
      checkAdditionalCsvErrors() {
        if (this.content.length < 2) {
          this.csvErrors.push("Too few lines. First line must contain headers and at least one line that contains data, must follow.");
        } else {
          const numColumns = this.content[0].length;
          if (numColumns < 2) {
            this.csvErrors.push("Too few columns. At least 2 columns are needed, for the latitude (y coordinate) and for the longitude (x coordinate).");
          } else {
            const linesWithTooFewCells = this.content.reduce((lines, line, i) => { if (line.length < numColumns) { lines.push(i); } return lines; }, []);
            const linesWithTooManyCells = this.content.reduce((lines, line, i) => { if (line.length > numColumns) { lines.push(i); } return lines; }, []);
            if (linesWithTooFewCells.length > 0) {
              this.csvErrors.push("Too few cells in line(s) " + linesWithTooFewCells.join(", ") + ". Exactly " + numColumns + " cells are needed.");
            }
            if (linesWithTooManyCells.length > 0) {
              this.csvErrors.push("Too many cells in line(s) " + linesWithTooManyCells.join(", ") + ". Exactly " + numColumns + " cells are needed.");
            }
          }
        }
      },
      guessColumns() {
        if (this.csvErrors.length > 0) {
          return;
        }

        this.content[0].forEach((header, i) => {
          const cleanHeader = header.toLowerCase().replace(/[^a-z]/g, "");
          if (["latitude", "lat"].includes(cleanHeader)) {
            this.latitudeColumn = i;
          }
          if (["longitude", "long", "lon"].includes(cleanHeader)) {
            this.longitudeColumn = i;
          }
        });
        this.content[0].forEach((header, i) => {
          const cleanHeader = header.toLowerCase().replace(/[^a-z]/g, "");
          if ((this.latitudeColumn == null) && ["y", "ycoord", "ycoordinate"].includes(cleanHeader)) {
            this.latitudeColumn = i;
          }
          if ((this.longitudeColumn == null) && ["x", "xcoord", "xcoordinate"].includes(cleanHeader)) {
            this.longitudeColumn = i;
          }
        });
      },
      validate(ll = false) {
        this.dataErrors = [];

        if (this.parseConfig.quoteChar != this.parseConfig.quoteChar.trim()) {
          if (this.parseConfig.quoteChar.length == 1) {
            this.dataErrors.push("Quote character cannot be a whitespace.");
          } else {
            this.dataErrors.push("Remove the whitespace around your quote character.");
          }
        } else if (this.parseConfig.quoteChar.length > 1) {
          this.dataErrors.push("Quote character cannot be more than one character long.");
        } else if (this.parseConfig.quoteChar == this.delimiter) {
          this.dataErrors.push("Quote character cannot be the same as delimiter.");
        }

        if (ll) {
          if (this.latitudeColumn == null) {
            this.dataErrors.push("No latitude column selected.");
          }
          if (this.longitudeColumn == null) {
            this.dataErrors.push("No longitude column selected.");
          } else if (this.latitudeColumn == this.longitudeColumn) {
            this.dataErrors.push("Latitude and longitude must be in separate columns.");
          }
        }

        let decimalComma = false;
        [
          {column: this.latitudeColumn, columnName: "Latitude", maxVal: 90},
          {column: this.longitudeColumn, columnName: "Longitude", maxVal: 180},
          {column: this.abstractionColumn, columnName: "Abstraction"},
          {column: this.effluentColumn, columnName: "Effluent"},
          {column: this.lossColumn, columnName: "Loss"}
        ].forEach(o => decimalComma = (this.validateNumber(o.column, o.columnName, o.maxVal) ? true : decimalComma));

        if (decimalComma) {
          this.dataErrors.push("A valid number is 123 or 12.3 but not 12,3 (decimal comma).");
        }
      },
      validateNumber(column, columnName, maxVal = null) {
        if ((column != null) && this.content.slice(1).some(row => isNaN(row[column]))) {
          this.dataErrors.push(columnName + " column contains invalid numbers.");
          return this.content.slice(1).some(row => row[column].match(/\d\,\d/));
        }
        if ((column != null) && (maxVal != null) && this.content.slice(1).some(row => Math.abs(row[column]) > maxVal)) {
          this.dataErrors.push(columnName + " must be a number between " + -maxVal + " and " + maxVal + ".");
          return false;
        }
        return false;
      },
      next() {
        this.validate(true);
        if (this.dataErrors.length == 0) {
          this.setTempScenario();
          this.close();
          openModal(modalTypes.scenario1, { tempScenario: this.tempScenario, width: 700/*, height: "auto", draggable: false, scrollable: true*/ });
        }
      },
      setTempScenario() {
        this.tempScenario.csvDelimiter = this.delimiter;
        this.tempScenario.csvQuoteChar = this.parseConfig.quoteChar || '"';
        this.tempScenario.csvHeaders = Papa.unparse([this.content[0]], this.unparseConfig);
        this.tempScenario.processes = this.content.slice(1).map(row => {
          return {
            name: (this.nameColumn != null)? row[this.nameColumn]: "no name",
            abstraction: (this.abstractionColumn != null)? row[this.abstractionColumn]: 0,
            effluent: (this.effluentColumn != null)? row[this.effluentColumn]: 0,
            loss: (this.lossColumn != null)? row[this.lossColumn]: 0,
            csv: Papa.unparse([row], this.unparseConfig),
            geometry: "SRID=4326;POINT(" + row[this.longitudeColumn] + " " + row[this.latitudeColumn] + ")"
          };
        });
      }
    }
  };
</script>

<style lang="scss" scoped>
  .preview {
    max-height: 180px;
    overflow: auto;
  }
  th, td {
    opacity: 0.3;
  }
  th.reqcol, td.reqcol {
    opacity: 1;
    background: var(--bs-highlight-bg);
    .sticker {
      background-color: var(--bs-orange);
    }
  }
  th.optcol, td.optcol {
    opacity: 1;
    .sticker {
      background-color: var(--bs-secondary);
    }
  }
  th .sticker {
    border-radius: 12px;
    padding: 2px 12px 3px;
    color: white;
  }
</style>
